<template>
    <div v-if="show" class="modal-overlay" @click="handleClose">
        <div class="modal-dialog" @click.stop>
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title">{{ title }}</h5>
                    <button type="button" class="close-btn" @click="handleClose" :disabled="loading">
                        <i class="fas fa-times"></i>
                    </button>
                </div>
                <div class="modal-body">
                    <!-- Loading state -->
                    <div v-if="loading" class="modal-loading">
                        <i class="fas fa-spinner fa-spin"></i>
                        <span>Fájl betöltése...</span>
                    </div>
                    <!-- Error state -->
                    <div v-else-if="error" class="modal-error">
                        <i class="fas fa-exclamation-triangle"></i>
                        <p>Hiba történt a fájl betöltése során</p>
                        <button class="btn btn-primary btn-sm" @click="$emit('retry')">
                            <i class="fas fa-redo"></i>
                            Újrapróbálás
                        </button>
                    </div>
                    <!-- File content -->
                    <div v-else-if="fileUrl">
                        <img v-if="isImage" :src="fileUrl" alt="File preview" class="modal-image" />
                        <iframe v-else-if="isPdf" :src="fileUrl" class="modal-iframe">
                        </iframe>
                        <div v-else class="unsupported-file">
                            <i class="fas fa-file-alt"></i>
                            <p>Ez a fájltípus nem tekinthető meg előnézetben.</p>
                            <a :href="fileUrl" :download="filename" class="btn btn-primary">
                                <i class="fas fa-download"></i>
                                Letöltés
                            </a>
                        </div>
                    </div>
                </div>
                <div class="modal-footer" v-if="!loading">
                    <a v-if="fileUrl && !error" :href="fileUrl" :download="filename"
                        class="btn btn-success btn-sm">
                        <i class="fas fa-download"></i>
                        Letöltés
                    </a>
                    <button type="button" class="btn btn-secondary" @click="handleClose">
                        Bezár
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'FileViewerModal',
    props: {
        show: {
            type: Boolean,
            default: false
        },
        fileUrl: {
            type: String,
            default: ''
        },
        filename: {
            type: String,
            default: ''
        },
        title: {
            type: String,
            default: 'Fájl megtekintés'
        },
        loading: {
            type: Boolean,
            default: false
        },
        error: {
            type: Boolean,
            default: false
        },
        isImage: {
            type: Boolean,
            default: false
        },
        isPdf: {
            type: Boolean,
            default: false
        }
    },
    emits: ['close', 'retry'],
    methods: {
        handleClose() {
            if (!this.loading) {
                this.$emit('close');
            }
        }
    },
    watch: {
        show(newVal) {
            // Prevent body scroll when modal is open
            if (newVal) {
                document.body.style.overflow = 'hidden';
            } else {
                document.body.style.overflow = '';
            }
        }
    },
    beforeUnmount() {
        // Cleanup on component destroy
        document.body.style.overflow = '';
    }
}
</script>

<style scoped>
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1050;
}

.modal-dialog {
    background: white;
    border-radius: 12px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
    max-width: 90vw;
    max-height: 90vh;
    width: 975px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.modal-content {
    display: flex;
    flex-direction: column;
    height: 100%;
}

.modal-header {
    padding: 20px 30px;
    border-bottom: 1px solid #dee2e6;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: #f8f9fa;
}

.modal-title {
    font-size: 18px;
    font-weight: 600;
    margin: 0;
    color: #333;
}

.close-btn {
    background: none;
    border: none;
    font-size: 20px;
    color: #6c757d;
    cursor: pointer;
    padding: 0;
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    transition: background-color 0.2s;
}

.close-btn:hover:not(:disabled) {
    background-color: #e9ecef;
    color: #333;
}

.close-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.modal-body {
    padding: 0;
    flex: 1;
    overflow: hidden;
    align-items: stretch;
    justify-content: center;
    min-height: 400px;
    width: 100%;
}

.modal-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 15px;
    color: #007bff;
    padding: 40px;
}

.modal-loading i {
    font-size: 32px;
    animation: spin 1s linear infinite;
}

.modal-error {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 15px;
    color: #dc3545;
    padding: 40px;
    text-align: center;
}

.modal-error i {
    font-size: 48px;
    margin-bottom: 10px;
}

.modal-image {
    width: 100%;
    height: auto;
    max-height: 70vh;
    object-fit: contain;
}

.modal-iframe {
    width: 100%;
    height: 70vh;
    border: none;
    min-width: 100%;
    box-sizing: border-box;
}

.unsupported-file {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
    padding: 40px;
    text-align: center;
    color: #6c757d;
}

.unsupported-file i {
    font-size: 64px;
    opacity: 0.5;
}

.modal-footer {
    padding: 20px 30px;
    border-top: 1px solid #dee2e6;
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    background: #f8f9fa;
}

.btn {
    padding: 8px 16px;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;
    transition: all 0.2s;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    text-decoration: none;
}

.btn-primary {
    background-color: #007bff;
    color: white;
}

.btn-primary:hover {
    background-color: #0056b3;
}

.btn-success {
    background-color: #28a745;
    color: white;
}

.btn-success:hover {
    background-color: #1e7e34;
}

.btn-secondary {
    background-color: #6c757d;
    color: white;
}

.btn-secondary:hover {
    background-color: #5a6268;
}

.btn-sm {
    padding: 6px 12px;
    font-size: 12px;
}

@keyframes spin {
    from {
        transform: rotate(0deg);
    }

    to {
        transform: rotate(360deg);
    }
}
</style>